/**
 * Phase 16F real-time push (REQ-NTF-12, 12b, 12c; plan §4 J and §14.3).
 *
 * - Connects with a single-use ticket (`${url}?ticket=…`); Cognito tokens never go in the URL.
 * - Reconnects with backoff 1 s → 2 → 4 … → 30 s.
 * - Sends `{ action: 'heartbeat' }` every 5 min (API Gateway idle limit is 10 min).
 * - Recycles the connection at 110 min (API Gateway closes it at 2 h).
 * - While disconnected, polls the New count every 60 s; on (re)connect it asks for a resync.
 * - `stop()` closes for good (sign-out, idle logout, lock, unmount).
 * With no URL configured (REACT_APP_WS_URL empty) it only polls.
 * Frames carry ids and counts only; nothing here is logged.
 */
import type { NotificationPushFrame } from '../../dtos/notifications/notification.dto';
import { parsePushFrame } from '../../dtos/notifications/notification.mapper';

export const HEARTBEAT_MS = 5 * 60 * 1000;
export const RECYCLE_MS = 110 * 60 * 1000;
export const POLL_MS = 60 * 1000;
export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 30 * 1000;

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closed';

type WebSocketCtor = new (url: string) => WebSocket;

export interface NotificationSocketOptions {
  url: string;
  getTicket: () => Promise<string>;
  /** A notifications.changed frame arrived. */
  onChanged: (frame: NotificationPushFrame) => void;
  /** Every 60 s while disconnected. */
  onPoll: () => void;
  /** After each successful (re)connect: refetch count and the open panel. */
  onResync: () => void;
  onStatus?: (status: SocketStatus) => void;
  WebSocketImpl?: WebSocketCtor;
}

/** Backoff delay for the n-th consecutive failure (0-based). */
export const backoffDelay = (attempt: number): number => Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));

export class NotificationSocket {
  private ws: WebSocket | null = null;
  private stopped = true;
  private attempts = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private recycleTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private generation = 0;
  private status: SocketStatus = 'idle';

  constructor(private readonly options: NotificationSocketOptions) {}

  get currentStatus(): SocketStatus {
    return this.status;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempts = 0;
    if (!this.options.url) {
      // No WebSocket endpoint for this build: polling only
      this.setStatus('closed');
      this.startPolling();
      return;
    }
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.generation += 1;
    this.clearTimers();
    this.stopPolling();
    this.closeSocket();
    this.setStatus('idle');
  }

  private setStatus(status: SocketStatus) {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }

  private async connect(): Promise<void> {
    const gen = ++this.generation;
    this.setStatus('connecting');
    let ticket: string;
    try {
      ticket = await this.options.getTicket();
    } catch {
      if (gen === this.generation && !this.stopped) this.handleDisconnect();
      return;
    }
    if (gen !== this.generation || this.stopped) return;

    const Impl: WebSocketCtor = this.options.WebSocketImpl || WebSocket;
    let ws: WebSocket;
    try {
      ws = new Impl(`${this.options.url}${this.options.url.includes('?') ? '&' : '?'}ticket=${encodeURIComponent(ticket)}`);
    } catch {
      this.handleDisconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (gen !== this.generation) return;
      this.attempts = 0;
      this.stopPolling();
      this.setStatus('open');
      this.heartbeatTimer = setInterval(() => this.send({ action: 'heartbeat' }), HEARTBEAT_MS);
      this.recycleTimer = setTimeout(() => this.recycle(), RECYCLE_MS);
      this.options.onResync();
    };
    ws.onmessage = (event: MessageEvent) => {
      if (gen !== this.generation) return;
      const frame = parsePushFrame(event.data);
      if (frame) this.options.onChanged(frame);
    };
    ws.onerror = () => {
      // onclose follows and handles the reconnect
    };
    ws.onclose = () => {
      if (gen !== this.generation) return;
      this.ws = null;
      this.handleDisconnect();
    };
  }

  private send(message: Record<string, string>) {
    if (this.ws && this.ws.readyState === 1 /* OPEN */) {
      try {
        this.ws.send(JSON.stringify(message));
      } catch {
        // The close handler takes care of it
      }
    }
  }

  /** Planned reconnect before API Gateway's 2-hour limit; no backoff, no polling gap to fill. */
  private recycle() {
    if (this.stopped) return;
    this.clearTimers();
    this.closeSocket();
    this.attempts = 0;
    void this.connect();
  }

  private handleDisconnect() {
    this.clearTimers();
    if (this.stopped) return;
    this.setStatus('closed');
    this.startPolling();
    const delay = backoffDelay(this.attempts);
    this.attempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) void this.connect();
    }, delay);
  }

  private startPolling() {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => this.options.onPoll(), POLL_MS);
  }

  private stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private clearTimers() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.recycleTimer) clearTimeout(this.recycleTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.heartbeatTimer = null;
    this.recycleTimer = null;
    this.reconnectTimer = null;
  }

  private closeSocket() {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close(1000);
    } catch {
      // Already closed
    }
  }
}

/** WebSocket endpoint for this build (wss://…); empty means polling only. */
export const notificationWsUrl = (): string => (process.env.REACT_APP_WS_URL || '').trim();
